import React, { useEffect, useMemo, useState, useCallback, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { apiUrl } from '../config/api';
import computeMetrics from '../utils/cardMetrics';

function monthName(m) {
  const names = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];
  const i = Math.max(1, Math.min(12, Number(m||0))) - 1;
  return names[i] || String(m);
}

const fmtNum = (v) => (v == null ? '—' : Number(v).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const buildPeriodKey = (year, month) => `${year}-${String(month).padStart(2, '0')}`;
const shiftPeriod = (year, month, deltaMonths) => {
  const baseIndex = Number(year) * 12 + (Number(month) - 1) + deltaMonths;
  const nextYear = Math.floor(baseIndex / 12);
  const nextMonth = ((baseIndex % 12) + 12) % 12 + 1;
  return { year: nextYear, month: nextMonth };
};

const calcClosingStatsFromRows = (rows = []) => {
  const totals = { invoiced: 0, debt: 0, unsigned: 0 };
  const byCard = new Map();
  rows.forEach((row) => {
    const amount = Number(row.amountCustomer ?? row.amount_customer ?? 0) || 0;
    const paid = Number(row.paidAmount ?? row.paid_amount ?? 0) || 0;
    const signed = Number(row.signedAmount ?? row.signed_amount ?? 0) || 0;
    const debt = Math.max(0, amount - paid);
    const unsigned = Math.max(0, amount - signed);
    totals.invoiced += amount;
    totals.debt += debt;
    totals.unsigned += unsigned;
    const cardIdRaw = row.cardId ?? row.card_id ?? row.cardID ?? row.cardid;
    const cardId = Number(cardIdRaw);
    if (Number.isFinite(cardId) && cardId > 0) {
      const bucket = byCard.get(cardId) || { invoiced: 0, debt: 0, unsigned: 0 };
      bucket.invoiced += amount;
      bucket.debt += debt;
      bucket.unsigned += unsigned;
      byCard.set(cardId, bucket);
    }
  });
  return { totals, byCard };
};

const debtBadgeStyle = (value) => {
  const isZero = Math.abs(Number(value) || 0) < 0.0001;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '2px 8px',
    borderRadius: 999,
    fontWeight: 700,
    background: isZero ? '#dcfce7' : '#fee2e2',
    color: isZero ? '#166534' : '#991b1b',
    minWidth: 86,
    textAlign: 'right',
  };
};
const renderDebtBadge = (value) => <span style={debtBadgeStyle(value)}>{fmtNum(value)}</span>;

export default function Supercontrol() {
  const API_BASE = apiUrl();
  const now = new Date();
  const [fromMonth, setFromMonth] = useState(1);
  const [fromYear, setFromYear] = useState(now.getFullYear());
  const [toMonth, setToMonth] = useState(12);
  const [toYear, setToYear] = useState(now.getFullYear());
  const [months, setMonths] = useState([]); // [{year,month,cardIds:[], metrics:{...}}]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cardsById, setCardsById] = useState(new Map());
  const [drillPivot, setDrillPivot] = useState(null); // {year, month, cards, metricsById}
  const mainScrollRef = useRef(null);
  const mainTopScrollRef = useRef(null);
  const mainTableRef = useRef(null);
  const [mainScrollWidth, setMainScrollWidth] = useState(0);
  const pivotScrollRef = useRef(null);
  const pivotTopScrollRef = useRef(null);
  const pivotTableRef = useRef(null);
  const [pivotScrollWidth, setPivotScrollWidth] = useState(0);

  // guard: only superusers
  let isSuper = false;
  try { const u = JSON.parse(localStorage.getItem('authUser') || 'null'); isSuper = ['1001','1002'].includes(String(u?.id)); } catch {}

  const periodNavButtonStyle = {
    width: 34,
    height: 34,
    borderRadius: 10,
    border: '1px solid #cbd5e1',
    background: '#fff',
    color: '#0f172a',
    cursor: 'pointer',
    fontSize: 18,
    lineHeight: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  };

  useEffect(() => { document.title = 'Суперконтроль'; }, []);

  const shiftRange = useCallback((deltaMonths) => {
    const nextFrom = shiftPeriod(fromYear, fromMonth, deltaMonths);
    const nextTo = shiftPeriod(toYear, toMonth, deltaMonths);
    setFromMonth(nextFrom.month);
    setFromYear(nextFrom.year);
    setToMonth(nextTo.month);
    setToYear(nextTo.year);
  }, [fromMonth, fromYear, toMonth, toYear]);

  const syncScrollLeft = useCallback((source, target) => {
    if (!source || !target) return;
    const next = source.scrollLeft;
    if (Math.abs(target.scrollLeft - next) > 1) {
      target.scrollLeft = next;
    }
  }, []);

  const handleMainScroll = useCallback(() => {
    syncScrollLeft(mainScrollRef.current, mainTopScrollRef.current);
  }, [syncScrollLeft]);

  const handleMainTopScroll = useCallback(() => {
    syncScrollLeft(mainTopScrollRef.current, mainScrollRef.current);
  }, [syncScrollLeft]);

  const handlePivotScroll = useCallback(() => {
    syncScrollLeft(pivotScrollRef.current, pivotTopScrollRef.current);
  }, [syncScrollLeft]);

  const handlePivotTopScroll = useCallback(() => {
    syncScrollLeft(pivotTopScrollRef.current, pivotScrollRef.current);
  }, [syncScrollLeft]);

  const load = useCallback(async () => {
    try {
      setLoading(true); setError('');
      const qs = new URLSearchParams({ fromMonth, fromYear, toMonth, toYear }).toString();
      const [sumRes, listRes] = await Promise.all([
        fetch(`${API_BASE}/api/supercontrol/summary?${qs}`).then(r=>r.json()),
        fetch(`${API_BASE}/api/card-calc?full=1&perPage=2000&withDelivered=1`).then(r=>r.json()),
      ]);
      const items = Array.isArray(listRes.items) ? listRes.items : (Array.isArray(listRes) ? listRes : []);
      const map = new Map(items.map(c => [c.id, c]));
      setCardsById(map);
      const ms = (sumRes?.months || []).map(m => ({ ...m }));

      const closingStatsMap = new Map();
      const uniquePeriods = [];
      const seenPeriods = new Set();
      for (const m of ms) {
        const key = buildPeriodKey(m.year, m.month);
        if (!seenPeriods.has(key)) {
          seenPeriods.add(key);
          uniquePeriods.push({ key, year: m.year, month: m.month });
        }
      }
      await Promise.all(uniquePeriods.map(async ({ key, year, month }) => {
        try {
          const resp = await fetch(`${API_BASE}/api/closing-docs?month=${month}&year=${year}`);
          if (!resp.ok) throw new Error('failed');
          const data = await resp.json();
          const stats = calcClosingStatsFromRows(Array.isArray(data.rows) ? data.rows : []);
          closingStatsMap.set(key, stats);
        } catch (err) {
          closingStatsMap.set(key, { totals: { invoiced: 0, debt: 0, unsigned: 0 }, byCard: new Map() });
        }
      }));

      // compute aggregated metrics per month
      for (const m of ms) {
        const cards = (m.cardIds || []).map(id => map.get(id)).filter(Boolean);
        const metricsList = cards.map(computeMetrics);
        const sum = (key) => metricsList.reduce((s, x) => s + (Number(x?.[key]) || 0), 0);
        const weighted = (key) => {
          let wsum = 0, w = 0; for (const x of metricsList) { const v = Number(x?.[key]) || 0; const wt = Number(x?.contractTotal)||0; wsum += v*wt; w += wt; } return w>0? (wsum/w):0;
        };
  const keysTriplets = [ ['fot_cash_sum','fot_cash_paid','fot_cash_remain'], ['fot_card_sum','fot_card_paid','fot_card_remain'], ['tmc_with_vat','tmc_paid_manual','tmc_remain'], ['extras_vat','extras_vat_paid','extras_vat_remain'], ['extras_no_vat','extras_no_vat_paid','extras_no_vat_remain'], ['extras_cash','extras_cash_paid','extras_cash_remain'] ];
        const agg = {};
  const allKeys = ['contractTotal','fot_total','ndfl','insurance','fot_tax','amort_month','vat_to_pay','vat_deduction','vat_delta','overhead','profit_tax','cost_price','net_profit','amort_purchase_total','break_even','cash_remain_total','subcontract_base','soap_profit_on_hand','vat_base_total','base_total','profit_on_hand','closing_invoiced','closing_debt','closing_unsigned','soap_share_frac'];
        for (const k of allKeys) agg[k] = sum(k);
        agg.profitability_pct = weighted('profitability_pct');
        agg.profit_on_hand_pct = weighted('profit_on_hand_pct');
        for (const [a,b,c] of keysTriplets) { agg[a] = sum(a); agg[b] = sum(b); agg[c] = sum(c); }
        const closingStats = closingStatsMap.get(buildPeriodKey(m.year, m.month)) || { totals: { invoiced: 0, debt: 0, unsigned: 0 }, byCard: new Map() };
        agg.closing_invoiced = closingStats.totals.invoiced;
        agg.closing_debt = closingStats.totals.debt;
        agg.closing_unsigned = closingStats.totals.unsigned;
        m.closingByCard = closingStats.byCard;
        m.metrics = agg;
      }
      setMonths(ms);
    } catch (e) {
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, [fromMonth, fromYear, toMonth, toYear, API_BASE]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => ([
    { key: 'contractTotal', title: 'Сумма контракта, руб.' },
    { key: 'profit_on_hand', title: 'Прибыль на руки, руб.' },
    { key: 'profit_on_hand_pct', title: 'Рентабельность чистая, %', isPercent: true },
    { key: 'closing_invoiced', title: 'Выставлено счетов (реестр)' },
    { key: 'closing_debt', title: 'Дебиторка по реестру', highlightDebt: true },
    { key: 'closing_unsigned', title: 'Неподписанные акты', highlightDebt: true },
    { key: 'fot_total', title: 'Общая сумма ФОТ (карта+нал)' },
    { key: 'fot_cash', title: 'Сумма ФОТ нал', triplet: ['fot_cash_sum', 'fot_cash_paid', 'fot_cash_remain'] },
    { key: 'fot_card', title: 'Сумма ФОТ на карту', triplet: ['fot_card_sum', 'fot_card_paid', 'fot_card_remain'] },
    { key: 'ndfl', title: 'НДФЛ, руб.' },
    { key: 'insurance', title: 'Страховые взносы' },
    { key: 'fot_tax', title: 'Сумма налогов на ФОТ, руб.' },
    { key: 'tmc_with_vat', title: 'ТМЦ (с НДС)', triplet: ['tmc_with_vat', 'tmc_paid_manual', 'tmc_remain'] },
    { key: 'amort_month', title: 'Амортизация оборудования и инвентаря' },
    { key: 'extras_vat', title: 'Доп.затраты, с НДС', triplet: ['extras_vat', 'extras_vat_paid', 'extras_vat_remain'] },
    { key: 'extras_no_vat', title: 'Доп.затраты, без НДС', triplet: ['extras_no_vat', 'extras_no_vat_paid', 'extras_no_vat_remain'] },
    { key: 'extras_cash', title: 'Доп.затраты, наличные', triplet: ['extras_cash', 'extras_cash_paid', 'extras_cash_remain'] },
    { key: 'vat_to_pay', title: 'НДС начисленный' },
    { key: 'vat_deduction', title: 'НДС в зачет' },
    { key: 'vat_delta', title: 'НДС на оплату' },
    { key: 'overhead', title: 'Накладные расходы' },
    { key: 'profit_tax', title: 'Налог на прибыль' },
    { key: 'cost_price', title: 'Себестоимость контракта' },
    { key: 'net_profit', title: 'Прибыль контракта' },
    { key: 'profitability_pct', title: 'Рентабельность, %', isPercent: true },
    { key: 'break_even', title: 'Точка безубыточности (справочно)' },
    { key: 'cash_remain_total', title: 'Итого осталось выплатить нал' },
    { key: 'subcontract_base', title: 'Субподряд На мыло (с НДС)' },
    { key: 'soap_profit_on_hand', title: 'Получено нал всего' },
    { key: 'vat_base_total', title: 'Чистая прибыль белая' },
    { key: 'base_total', title: 'ИТОГО затраты с налогами (без накладных)' },
    { key: 'soap_share_frac', title: 'Процент выручки минус затраты → мыло' },
  ]), []);

  const pivotContext = useMemo(() => {
    if (!drillPivot) return null;
    const mm = months.find((x) => x.year === drillPivot.year && x.month === drillPivot.month);
    if (!mm) return null;
    const ids = Array.isArray(mm.cardIds) ? mm.cardIds : [];
    const cols = ids.map((id) => cardsById.get(id)).filter(Boolean);
    if (!cols.length) return { cols: [], metricsById: new Map(), totalsP: {}, closingByCard: new Map(), tableMinWidth: 0 };
    const metricsById = new Map(cols.map((card) => [card.id, computeMetrics(card)]));
    const closingByCard = mm.closingByCard instanceof Map ? mm.closingByCard : new Map();
    for (const [cardId, metrics] of metricsById.entries()) {
      const closing = closingByCard.get(cardId) || { invoiced: 0, debt: 0, unsigned: 0 };
      metrics.closing_invoiced = closing.invoiced;
      metrics.closing_debt = closing.debt;
      metrics.closing_unsigned = closing.unsigned;
    }
    const totalsP = (() => {
      const t = {};
      const metricsList = cols.map((c) => metricsById.get(c.id)).filter(Boolean);
      const sum = (key) => metricsList.reduce((s, m) => s + (Number(m?.[key]) || 0), 0);
      const weighted = (key) => {
        let wsum = 0;
        let w = 0;
        for (const m of metricsList) {
          const v = Number(m?.[key]) || 0;
          const wt = Number(m?.contractTotal) || 0;
          wsum += v * wt;
          w += wt;
        }
        return w > 0 ? wsum / w : 0;
      };
      for (const r of rows) {
        if (r.triplet) {
          const [a, b, c] = r.triplet;
          t[r.key] = { a: sum(a), b: sum(b), c: sum(c) };
        } else if (r.isPercent) {
          t[r.key] = weighted(r.key);
        } else {
          t[r.key] = sum(r.key);
        }
      }
      return t;
    })();
    const tableMinWidth = Math.max(900, 260 + 180 + cols.length * 180);
    return { cols, metricsById, totalsP, closingByCard, tableMinWidth };
  }, [drillPivot, months, cardsById, rows]);

  useEffect(() => {
    if (!pivotContext) return;
    const updateWidth = () => {
      const width = pivotTableRef.current?.scrollWidth || 0;
      setPivotScrollWidth(width);
      if (pivotTopScrollRef.current && pivotScrollRef.current) {
        pivotTopScrollRef.current.scrollLeft = pivotScrollRef.current.scrollLeft;
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [pivotContext]);

  // header labels формируются на лету из months

  const totals = useMemo(() => {
    const t = {};
    const sum = (arr, key) => arr.reduce((s, m) => s + (Number(m?.metrics?.[key]) || 0), 0);
    const weighted = (arr, key) => {
      let wsum = 0, w = 0;
      for (const m of arr) {
        const val = Number(m?.metrics?.[key]) || 0;
        const weight = Number(m?.metrics?.contractTotal) || 0;
        wsum += val * weight; w += weight;
      }
      return w > 0 ? (wsum / w) : 0;
    };
    for (const r of rows) {
      if (r.triplet) {
        const [a,b,c] = r.triplet; t[r.key] = { a: sum(months,a), b: sum(months,b), c: sum(months,c) };
      } else if (r.isPercent) {
        t[r.key] = weighted(months, r.key);
      } else {
        t[r.key] = sum(months, r.key);
      }
    }
    return t;
  }, [rows, months]);

  const profitStyle = (value, { isPercent = false } = {}) => {
    const v = Number(value) || 0; const abs = Math.abs(v);
    const max = isPercent ? 30 : 1_000_000; const t = Math.max(0, Math.min(1, abs / max));
    const fill = Math.round(t * 100); const alpha = 0.12 + t * 0.38;
    const pos = v > 0; const base = pos ? `rgba(34,197,94,${alpha})` : `rgba(239,68,68,${alpha})`;
    const text = pos ? '#065f46' : '#7f1d1d'; const border = pos ? '1px solid rgba(16,185,129,0.35)' : '1px solid rgba(239,68,68,0.35)';
    return { background: `linear-gradient(90deg, ${base} ${fill}%, rgba(0,0,0,0) ${fill}%)`, color: text, border, borderRadius: 6, padding: '2px 6px', display: 'inline-block', minWidth: 86, textAlign: 'right' };
  };

  const TripletCell = ({ m, keys }) => {
    const [sumK, , leftK] = keys;
    const sumV = m?.metrics?.[sumK];
    const leftV = m?.metrics?.[leftK];
    const bgLeft = (n) => { const r = Number(n) || 0; const eps = 0.005; if (r > eps) return '#ffe9d9'; if (r < -eps) return '#fff3cf'; return '#e8f7e5'; };
    return (
      <div style={{ display: 'grid', gridTemplateRows: '1fr 1fr', gap: 2 }}>
        <div style={{ fontSize: 13.5, color: '#111827', fontWeight: 700 }}>∑ {fmtNum(sumV)}</div>
        <div style={{ fontSize: 12, background: bgLeft(leftV), padding: '1px 4px', borderRadius: 4, justifySelf: 'end' }}>ост. {fmtNum(leftV)}</div>
      </div>
    );
  };

  const TripletTotal = ({ rowKey }) => {
    const v = totals[rowKey] || { a: 0, b: 0, c: 0 };
    const bgLeft = (n) => { const r = Number(n) || 0; const eps = 0.005; if (r > eps) return '#ffe9d9'; if (r < -eps) return '#fff3cf'; return '#e8f7e5'; };
    return (
      <div style={{ display: 'grid', gridTemplateRows: '1fr 1fr', gap: 2 }}>
        <div style={{ fontSize: 13.5, color: '#111827', fontWeight: 800 }}>∑ {fmtNum(v.a)}</div>
        <div style={{ fontSize: 12, background: bgLeft(v.c), padding: '1px 4px', borderRadius: 4, justifySelf: 'end', fontWeight: 700 }}>ост. {fmtNum(v.c)}</div>
      </div>
    );
  };

  const gridColor = 'var(--border-200, #d7dce8)';
  const headerBg = '#f5f5f5';
  const totalColBg = '#fffdf4';
  const mainTableMinWidth = Math.max(900, 260 + 160 + months.length * 160);
  const mainTableBaseStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    border: `1px solid ${gridColor}`,
    boxShadow: 'var(--shadow-1, 0 22px 44px rgba(15,23,42,0.08))',
  };
  const stickyCornerHeadStyle = {
    padding: 10,
    textAlign: 'left',
    border: `1px solid ${gridColor}`,
    position: 'sticky',
    top: 0,
    left: 0,
    zIndex: 6,
    background: headerBg,
    boxShadow: '0 2px 0 rgba(0,0,0,0.04)',
  };
  const stickyTotalHeadStyle = {
    padding: 10,
    textAlign: 'right',
    border: `1px solid ${gridColor}`,
    position: 'sticky',
    top: 0,
    zIndex: 5,
    background: '#fff8e5',
    fontWeight: 800,
    boxShadow: '0 2px 0 rgba(0,0,0,0.04)',
  };
  const monthHeadStyle = {
    padding: 10,
    textAlign: 'center',
    border: `1px solid ${gridColor}`,
    position: 'sticky',
    top: 0,
    background: headerBg,
    boxShadow: '0 2px 0 rgba(0,0,0,0.04)',
  };
  const stickyRowLabelStyle = {
    padding: 10,
    textAlign: 'left',
    fontWeight: 700,
    border: `1px solid ${gridColor}`,
    position: 'sticky',
    left: 0,
    zIndex: 3,
    boxShadow: '2px 0 0 rgba(0,0,0,0.04)',
  };
  const totalCellStyle = {
    padding: 10,
    textAlign: 'right',
    verticalAlign: 'top',
    border: `1px solid ${gridColor}`,
    background: totalColBg,
  };
  const valueCellStyle = {
    padding: 10,
    textAlign: 'right',
    verticalAlign: 'top',
    border: `1px solid ${gridColor}`,
    cursor: 'default',
  };

  useLayoutEffect(() => {
    const updateWidth = () => {
      const width = mainTableRef.current?.scrollWidth || 0;
      setMainScrollWidth(width);
      if (mainTopScrollRef.current && mainScrollRef.current) {
        mainTopScrollRef.current.scrollLeft = mainScrollRef.current.scrollLeft;
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [months, mainTableMinWidth]);

  if (!isSuper) {
    return <div style={{ padding: 24, color: '#c00' }}>Доступно только суперпользователям.</div>;
  }

  return (
    <div style={{ padding: '14px 0 0 0', marginTop: -18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <Link to="/">&larr; В меню</Link>
        <h2 style={{ margin: 0 }}>Суперконтроль — свод по актуальным карточкам</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" onClick={() => shiftRange(-12)} style={periodNavButtonStyle} title="Предыдущий год" aria-label="Предыдущий год">
            &laquo;
          </button>
          <button type="button" onClick={() => shiftRange(-1)} style={periodNavButtonStyle} title="Предыдущий месяц" aria-label="Предыдущий месяц">
            &lsaquo;
          </button>
          <span>Период:</span>
          <select value={fromMonth} onChange={e=>setFromMonth(Number(e.target.value))}>{Array.from({length:12},(_,i)=>i+1).map(m=><option key={m} value={m}>{String(m).padStart(2,'0')}</option>)}</select>
          <input type="number" value={fromYear} onChange={e=>setFromYear(Number(e.target.value))} style={{ width: 90 }} />
          <span>—</span>
          <select value={toMonth} onChange={e=>setToMonth(Number(e.target.value))}>{Array.from({length:12},(_,i)=>i+1).map(m=><option key={m} value={m}>{String(m).padStart(2,'0')}</option>)}</select>
          <input type="number" value={toYear} onChange={e=>setToYear(Number(e.target.value))} style={{ width: 90 }} />
          <button type="button" onClick={() => shiftRange(1)} style={periodNavButtonStyle} title="Следующий месяц" aria-label="Следующий месяц">
            &rsaquo;
          </button>
          <button type="button" onClick={() => shiftRange(12)} style={periodNavButtonStyle} title="Следующий год" aria-label="Следующий год">
            &raquo;
          </button>
          <button onClick={load} disabled={loading} style={{ padding: '6px 12px' }}>Обновить</button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center' }}>Загрузка…</div>
      ) : error ? (
        <div style={{ textAlign: 'center', color: '#c00' }}>{error}</div>
      ) : (
        <>
          <div
            style={{
              overflowX: 'auto',
              overflowY: 'hidden',
              height: 14,
              margin: '6px 0',
              borderRadius: 40,
              background: '#e2e8f0',
              scrollbarGutter: 'stable'
            }}
            ref={mainTopScrollRef}
            onScroll={handleMainTopScroll}
          >
            <div style={{ width: Math.max(mainScrollWidth, mainTableMinWidth), height: 8 }} />
          </div>
          <div
            style={{
              overflow: 'auto',
              maxHeight: 'calc(100vh - 140px)',
              position: 'relative',
              borderRadius: 18,
              background: 'var(--bg-card, #fff)',
              scrollbarGutter: 'stable'
            }}
            ref={mainScrollRef}
            onScroll={handleMainScroll}
          >
            <table ref={mainTableRef} style={{ ...mainTableBaseStyle, minWidth: mainTableMinWidth }}>
            <colgroup>
              <col style={{ width: 260 }} />
              <col style={{ width: 170 }} />
              {months.map((_, i) => (<col key={i} style={{ width: 150 }} />))}
            </colgroup>
            <thead>
              <tr>
                <th style={stickyCornerHeadStyle}></th>
                <th style={stickyTotalHeadStyle}>ИТОГО</th>
                {months.map((m, i) => (
                  <th key={i} style={{ ...monthHeadStyle, zIndex: 4 }}>
                    <a href={`/supercontrol/pivot?year=${encodeURIComponent(m.year)}&month=${encodeURIComponent(m.month)}`} target="_blank" rel="noreferrer" style={{ color:'#2563eb', textDecoration:'underline' }} title="Открыть разрез по карточкам в новой вкладке">
                      {monthName(m.month)} {m.year}
                    </a>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, rowIdx) => {
                const rowBg = rowIdx % 2 === 0 ? '#ffffff' : '#f8f9fb';
                const highlightDebt = Boolean(r.highlightDebt);
                return (
                  <tr key={r.key}>
                    <td style={{ ...stickyRowLabelStyle, background: rowBg }}>{r.title}</td>
                    <td style={totalCellStyle}>
                      {r.triplet ? (
                        <TripletTotal rowKey={r.key} />
                      ) : r.key === 'profit_on_hand' ? (
                        <span style={{ ...profitStyle(totals[r.key], { isPercent: false }), fontWeight: 700 }}>{fmtNum(totals[r.key])}</span>
                      ) : r.key === 'profit_on_hand_pct' ? (
                        <span style={{ ...profitStyle(totals[r.key], { isPercent: true }), fontWeight: 700 }}>{fmtNum(totals[r.key])}%</span>
                      ) : highlightDebt ? (
                        renderDebtBadge(totals[r.key])
                      ) : (
                        <span style={{ fontWeight: 700 }}>{fmtNum(totals[r.key])}</span>
                      )}
                    </td>
                    {months.map((m, i) => (
                      <td
                        key={i}
                        style={{ ...valueCellStyle, background: rowBg }}
                      >
                        {r.triplet ? (
                          <TripletCell m={m} keys={r.triplet} />
                        ) : r.key === 'profit_on_hand' ? (
                          <span style={profitStyle(m?.metrics?.[r.key], { isPercent: false })}>{fmtNum(m?.metrics?.[r.key])}</span>
                        ) : r.key === 'profit_on_hand_pct' ? (
                          <span style={profitStyle(m?.metrics?.[r.key], { isPercent: true })}>{fmtNum(m?.metrics?.[r.key])}%</span>
                        ) : highlightDebt ? (
                          renderDebtBadge(m?.metrics?.[r.key])
                        ) : (
                          fmtNum(m?.metrics?.[r.key])
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
            </table>
          </div>
        </>
      )}

      {drillPivot && (
        <div onClick={()=>setDrillPivot(null)} style={{ position: 'fixed', inset: 0, background: '#0008', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={(e)=>e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: 'min(1200px, 96vw)', maxHeight: '84vh', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h3 style={{ margin: 0 }}>Разрез по карточкам: {String(drillPivot.month).padStart(2,'0')}.{drillPivot.year}</h3>
            {pivotContext && pivotContext.cols.length ? (
              <>
                <div
                  style={{
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    paddingBottom: 2,
                    borderRadius: 10,
                    border: '1px solid #e5e7eb',
                    background: '#f8fafc',
                    scrollbarGutter: 'stable'
                  }}
                  ref={pivotTopScrollRef}
                  onScroll={handlePivotTopScroll}
                >
                  <div style={{ width: Math.max(pivotScrollWidth, pivotContext.tableMinWidth), height: 1 }} />
                </div>
                <div
                  style={{ overflow: 'auto', borderRadius: 12, background: '#fff', scrollbarGutter: 'stable', maxHeight: '60vh' }}
                  ref={pivotScrollRef}
                  onScroll={handlePivotScroll}
                >
                  <table ref={pivotTableRef} style={{ ...mainTableBaseStyle, minWidth: pivotContext.tableMinWidth }}>
                    <colgroup>
                      <col style={{ width: 260 }} />
                      <col style={{ width: 170 }} />
                      {pivotContext.cols.map((_, i) => (<col key={i} style={{ width: 180 }} />))}
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={stickyCornerHeadStyle}></th>
                        <th style={stickyTotalHeadStyle}>ИТОГО</th>
                        {pivotContext.cols.map((c, i) => (
                          <th key={c.id || i} style={{ ...monthHeadStyle, zIndex: 4, whiteSpace: 'normal' }}>
                            <div style={{ fontWeight: 800 }}>{c.objectName}</div>
                            <div style={{ fontSize: 12 }}>
                              <a href={`/card-calc/${encodeURIComponent(c.objectCodeFull || c.objectCode)}`} target="_blank" rel="noreferrer">
                                {c.objectCodeFull || String(c.objectCode).padStart(4, '0')}
                              </a>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, rowIdx) => {
                        const rowBg = rowIdx % 2 === 0 ? '#ffffff' : '#f8f9fb';
                        return (
                          <tr key={r.key}>
                            <td style={{ ...stickyRowLabelStyle, background: rowBg }}>{r.title}</td>
                            <td style={totalCellStyle}>
                              {r.triplet ? (
                                (() => {
                                  const v = pivotContext.totalsP[r.key] || { a: 0, b: 0, c: 0 };
                                  const bg = (n) => {
                                    const x = Number(n) || 0;
                                    const eps = 0.005;
                                    if (x > eps) return '#ffe9d9';
                                    if (x < -eps) return '#fff3cf';
                                    return '#e8f7e5';
                                  };
                                  return (
                                    <div style={{ display: 'grid', gridTemplateRows: '1fr 1fr 1fr', gap: 2 }}>
                                      <div style={{ fontSize: 12, color: '#374151' }}>∑ {fmtNum(v.a)}</div>
                                      <div style={{ fontSize: 12, color: '#166534' }}>выпл. {fmtNum(v.b)}</div>
                                      <div style={{ fontSize: 12, background: bg(v.c), padding: '1px 4px', borderRadius: 4, justifySelf: 'end' }}>ост. {fmtNum(v.c)}</div>
                                    </div>
                                  );
                                })()
                              ) : r.key === 'profit_on_hand' ? (
                                <span style={{ ...profitStyle(pivotContext.totalsP[r.key], { isPercent: false }), fontWeight: 700 }}>{fmtNum(pivotContext.totalsP[r.key])}</span>
                              ) : r.key === 'profit_on_hand_pct' ? (
                                <span style={{ ...profitStyle(pivotContext.totalsP[r.key], { isPercent: true }), fontWeight: 700 }}>{fmtNum(pivotContext.totalsP[r.key])}%</span>
                              ) : r.highlightDebt ? (
                                renderDebtBadge(pivotContext.totalsP[r.key])
                              ) : (
                                <span style={{ fontWeight: 700 }}>{fmtNum(pivotContext.totalsP[r.key])}</span>
                              )}
                            </td>
                            {pivotContext.cols.map((c, i) => {
                              const mmc = pivotContext.metricsById.get(c.id) || {};
                              return (
                                <td key={c.id || i} style={{ ...valueCellStyle, cursor: 'default', background: rowBg }}>
                                  {r.triplet ? (
                                    (() => {
                                      const [a, b, c3] = r.triplet;
                                      const bg = (n) => {
                                        const x = Number(n) || 0;
                                        const eps = 0.005;
                                        if (x > eps) return '#ffe9d9';
                                        if (x < -eps) return '#fff3cf';
                                        return '#e8f7e5';
                                      };
                                      return (
                                        <div style={{ display: 'grid', gridTemplateRows: '1fr 1fr 1fr', gap: 2 }}>
                                          <div style={{ fontSize: 12, color: '#374151' }}>∑ {fmtNum(mmc[a])}</div>
                                          <div style={{ fontSize: 12, color: '#166534' }}>выпл. {fmtNum(mmc[b])}</div>
                                          <div style={{ fontSize: 12, background: bg(mmc[c3]), padding: '1px 4px', borderRadius: 4, justifySelf: 'end' }}>ост. {fmtNum(mmc[c3])}</div>
                                        </div>
                                      );
                                    })()
                                  ) : r.key === 'profit_on_hand' ? (
                                    <span style={profitStyle(mmc[r.key], { isPercent: false })}>{fmtNum(mmc[r.key])}</span>
                                  ) : r.key === 'profit_on_hand_pct' ? (
                                    <span style={profitStyle(mmc[r.key], { isPercent: true })}>{fmtNum(mmc[r.key])}%</span>
                                  ) : r.highlightDebt ? (
                                    renderDebtBadge(mmc[r.key])
                                  ) : (
                                    fmtNum(mmc[r.key])
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div style={{ padding: 12, color: '#64748b' }}>Нет карточек для выбранного месяца.</div>
            )}
            <div style={{ textAlign: 'right' }}>
              <button onClick={()=>setDrillPivot(null)} style={{ padding: '6px 12px' }}>Закрыть</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
